import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { fetchCardByUsername, fetchCardLinks, fetchCardRedirect, fetchPublicUser, recordCardVisit, recordLinkClick } from "../utils/api";
import Skeleton, { CardSkeleton } from "./Skeleton";
import CardRenderer, { CardRendererCard, CardRendererLink } from "./CardRenderer";

const PublicCard: React.FC = () => {
    const { isSignedIn } = useUser();
    const { username } = useParams<{ username: string }>();
    const [card, setCard] = useState<CardRendererCard | null>(null);
    const [bio, setBio] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [links, setLinks] = useState<CardRendererLink[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isPro, setIsPro] = useState(false);
    const [resolvedTheme, setResolvedTheme] = useState('default');

    const searchParams = new URLSearchParams(window.location.search);
    const themeParam = searchParams.get('theme');

    useEffect(() => {
        const loadCard = async () => {
            if (!username) {
                setError("Invalid card URL");
                setLoading(false);
                return;
            }

            try {
                const cardData = await fetchCardByUsername(username);
                // BL-008: the public identifier is the account handle (the route
                // param), not the card's internal slug. Override so all
                // CardRenderer-derived URLs (share link, contact-form route,
                // resume) use the handle the visitor arrived on. Legacy slug
                // visits are redirected to the handle URL before reaching here.
                setCard({ ...cardData, username });

                setResolvedTheme(themeParam || cardData.theme || 'default');

                try {
                    // Public-safe projection scoped to this card so the server
                    // returns the address only if this card opts to show it.
                    const userData = await fetchPublicUser(cardData.userId, cardData.id);
                    if (userData.bio) setBio(userData.bio);
                    if (userData.shippingAddress) setAddress(userData.shippingAddress);
                    setIsPro(userData.isPro);
                } catch {}

                const refParam = new URLSearchParams(window.location.search).get('ref');
                recordCardVisit(cardData.id, refParam || undefined).catch(() => {});

                const cardLinks = await fetchCardLinks(cardData.id);
                setLinks(cardLinks.sort((a, b) => a.order - b.order) as CardRendererLink[]);
            } catch (err) {
                try {
                    const redirect = await fetchCardRedirect(username);
                    window.location.replace(`/card/${redirect.redirectTo}`);
                    return;
                } catch {
                    setError("Card not found");
                }
            } finally {
                setLoading(false);
            }
        };

        loadCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [username]);

    useEffect(() => {
        if (!card) return;

        const originalTitle = document.title;
        document.title = `${card.name} - WhoBrew`;

        let metaDesc = document.querySelector('meta[name="description"]');
        const originalDesc = metaDesc?.getAttribute('content') || '';
        if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.setAttribute('name', 'description');
            document.head.appendChild(metaDesc);
        }
        metaDesc.setAttribute('content', `${card.name} - ${card.position}. View their digital business card on WhoBrew.`);

        return () => {
            document.title = originalTitle;
            if (metaDesc) metaDesc.setAttribute('content', originalDesc);
        };
    }, [card]);

    const handleLinkClick = (link: CardRendererLink) => {
        recordLinkClick(link.id).catch(() => {});
    };
    // The legacy generic-Connect form was removed from CardRenderer; owners
    // configure their own contact_form-typed link in /my-card and visitor
    // submissions land via POST /api/cardlinks/:id/submit (see
    // submitCardLinkForm in api.ts). No matching handler needed here.

    if (loading) {
        return (
            <div className="min-h-screen bg-cream-50 dark:bg-coffee-900">
                <div className="max-w-md mx-auto px-4 py-8">
                    <div className="h-20 rounded-t-lg bg-cream-100 dark:bg-coffee-800 animate-pulse mb-0" />
                    <div className="text-center -mt-9 mb-6 px-4">
                        <Skeleton variant="circular" width="72px" height="72px" className="mx-auto mb-3" />
                        <Skeleton width="160px" height="22px" className="mx-auto mb-2" />
                        <Skeleton width="120px" height="16px" className="mx-auto mb-1" />
                        <Skeleton width="140px" height="12px" className="mx-auto" />
                    </div>
                    <CardSkeleton />
                </div>
            </div>
        );
    }

    if (error || !card) {
        return (
            <div className="min-h-screen bg-cream-50 dark:bg-coffee-900 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto px-6 py-20">
                    <p className="font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 mb-4">404</p>
                    <h1 className="font-display text-4xl sm:text-display-md text-coffee-900 dark:text-cream-50 tracking-tight">
                        Card not found.
                    </h1>
                    <p className="mt-6 text-body text-coffee-700 dark:text-cream-200 max-w-sm mx-auto">
                        This card doesn't exist, or it's been removed by its owner.
                    </p>
                    <div className="mt-10 flex flex-wrap justify-center gap-3">
                        <a
                            href="/"
                            className="inline-flex items-center justify-center h-12 px-6 rounded bg-coffee-900 text-cream-50 text-sm font-medium hover:bg-coffee-800 transition-colors"
                        >
                            Back to WhoBrew
                        </a>
                        <a
                            href="/contact"
                            className="inline-flex items-center justify-center h-12 px-6 rounded border border-coffee-900 dark:border-cream-50 text-coffee-900 dark:text-cream-50 text-sm font-medium hover:bg-coffee-900 hover:text-cream-50 transition-colors"
                        >
                            Contact us
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <CardRenderer
            card={card}
            links={links}
            bio={bio}
            address={address}
            isPro={isPro}
            resolvedTheme={resolvedTheme}
            isSignedIn={!!isSignedIn}
            onLinkClick={handleLinkClick}
        />
    );
};

export default PublicCard;
